/// <reference lib="dom" />
/** Brand-aware browser global key helper: reads `ACTIVE_BRAND.globalNamespacePrefix` (`const/brand-registry.ts`, the
 *  build-time selector over `const/brand.ts`'s identity records) at call time, so one `ACTIVE_BRAND_NAME` swap re-skins
 *  every namespace routed through it.
 *
 *  CONST-HOMED, and the ground is REACH rather than taste: 8 of
 *  `QUAL-BRAND-GLOBAL-PREFIX-HARDCODED-001`'s 18 day-one queue members live in `const/` leaves
 *  (`shared-explore-socket`, `shared-property-socket`, `websocket-actions-dom`), and no production `const/` leaf may
 *  import `@federa/utils` -- so the rule's own fix directive was unreachable for 44% of its queue and the promotion
 *  pledge could never discharge. `globalKey` depends only on the brand const, so `const/` is the one home every
 *  consumer layer can reach (the same layer-floor argument as `const/citation-normalize.ts`).
 *
 *  ADOPTION SURFACE (revised in the same audit; the prior "do NOT extend to typed member access" boundary was
 *  superseded by the rule's own fix the day it shipped): untyped index-access sites adopt directly; TYPED member
 *  access adopts through `Reflect.get(window, globalKey('...'))` (live: `components/common/federa-shell-reader.ts`),
 *  which reads the same property without the banned double cast. Ambient `Window` declarations remain structurally
 *  unreachable -- a `declare global` property name must be a literal or template-literal TYPE -- and are the rule's
 *  recorded structural carve-out, not adopters-in-waiting. */

import { ACTIVE_BRAND } from './brand-registry.ts';

/** e.g. `globalKey('Auth')` -> `'__federaAuth'`.
 *
 *  @runtime-derived-ssot-helper (const-leaf-pinned): reads `ACTIVE_BRAND.globalNamespacePrefix` at call time, and 8 of
 *  its adopter queue are `const/` leaves that may not import `@federa/utils` at all. Consumer evidence: this leaf's
 *  header docstring. */
export function globalKey(suffix: string): string {
	return `__${ACTIVE_BRAND.globalNamespacePrefix}${suffix}`;
}

/** e.g. `brandNamespaced('auth-modal-open')` -> `'federa:auth-modal-open'`.
 *
 *  THE SECOND BROWSER-SURFACE NAMESPACE GRAMMAR, and it is a sibling of {@link globalKey} rather than a case of it: a
 *  `window` KEY is `__<prefix><PascalSuffix>` because it has to be a valid identifier-ish property, while a DOM
 *  CustomEvent type and a Web-Storage key are colon-scoped kebab names. Routing an event name through `globalKey` would
 *  RENAME the wire contract to fit an identifier grammar it never needed; routing it through nothing left the brand
 *  hand-typed, which is the white-label break `QUAL-BRAND-GLOBAL-PREFIX-HARDCODED-001` describes and -- because its key
 *  grammar requires the `__` head and a PascalCase suffix -- cannot see.
 *
 *  @runtime-derived-ssot-helper (const-leaf-pinned): reads `ACTIVE_BRAND.globalNamespacePrefix` at call time, so one
 *  `ACTIVE_BRAND_NAME` swap re-skins every event name and storage key routed through it. Consumers: the auth-modal-open
 *  CustomEvent (`components/hooks/useAuthModal.ts`) and the explore scroll snapshot's storage key
 *  (`components/hooks/useExploreScrollRestoration-helpers.ts`). */
export function brandNamespaced(suffix: string): string {
	return `${ACTIVE_BRAND.globalNamespacePrefix}:${suffix}`;
}
